import greenCheckIcon from 'assets/images/green-check.svg';
import redXIcon from 'assets/images/red-x.svg';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentHeading from 'components/Heading/Heading';

import type { ContentfulComponentComparisonTable, ContentfulElementCell } from 'graphqlTypes';
import type { FC } from 'react';

interface StatisticsPanelProps extends FlexProps, ContentfulComponentComparisonTable {
  backgroundIsDark: boolean;
  comparisonHeading?: ContentfulComponentComparisonTable['heading'];
}

const ComponentComparisonTable: FC<StatisticsPanelProps> = ({
  comparisonHeading,
  companyLogos,
  row,
  backgroundIsDark,
  ...props
}) => {
  const checkHeadingMark = (value: ContentfulElementCell, index: number) => {
    const noMarked = index !== 0 && value?.heading !== 'Green Check' && value?.heading !== 'Red X';

    switch (value?.heading) {
      case 'Green Check':
        return (
          <Flex key={value?.id} width="100%" maxWidth="67px" justifyContent="center" sm={{ maxWidth: '170px' }}>
            <Container width="40px" height="40px">
              <Image image={{ file: { url: greenCheckIcon } }} alt="Green Check" height="100%" width="100%" />
            </Container>
          </Flex>
        );
      case 'Red X':
        return (
          <Flex key={value?.id} width="100%" maxWidth="67px" justifyContent="center" sm={{ maxWidth: '170px' }}>
            <Container width="40px" height="40px">
              <Image image={{ file: { url: redXIcon } }} alt="Red X" height="100%" width="100%" />
            </Container>
          </Flex>
        );
      default:
        return (
          <Flex
            key={value?.id}
            justifyContent={noMarked ? 'center' : 'flex-start'}
            width="100%"
            maxWidth={noMarked ? '67px' : '100%'}
            sm={{ maxWidth: noMarked ? '170px' : '100%' }}
          >
            <Text
              as="h3"
              textStyle="textMd"
              textColor="gray-900"
              fontWeight="bold"
              md={{ textStyle: 'textLg', fontWeight: 'bold' }}
            >
              {value?.heading}
            </Text>
          </Flex>
        );
    }
  };

  return (
    <Flex flexDirection="column" gap="40px" sm={{ gap: '56px' }} lg={{ gap: '64px' }} {...props}>
      <Flex justifyContent="space-between" gap="32px" flexDirection="column" md={{ flexDirection: 'row' }}>
        {comparisonHeading && <ComponentHeading isLeft {...comparisonHeading} backgroundIsDark={backgroundIsDark} />}
        {companyLogos && (
          <Flex
            gap="24px"
            alignItems="center"
            justifyContent={companyLogos.length === 1 ? 'flex-start' : 'center'}
            sm={{ gap: '30px' }}
          >
            {companyLogos.map(item => (
              <Container key={item?.id} width="152.5px" height="152.5px" sm={{ width: '170px', height: '170px' }}>
                <Image image={item as ImageData} alt={item?.title || 'Company Logo'} height="100%" width="100%" />
              </Container>
            ))}
          </Flex>
        )}
      </Flex>
      {row && (
        <Flex flexWrap="wrap">
          {row.map(
            (item, idx) =>
              item && (
                <Flex
                  key={item?.id}
                  width="100%"
                  paddingAll={16}
                  justifyContent="space-between"
                  backgroundColor={idx % 2 === 0 ? 'gray-50' : 'common-transparent'}
                >
                  {item.cell?.map((cell, subIdx) => cell && checkHeadingMark(cell, subIdx))}
                </Flex>
              ),
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default ComponentComparisonTable;
